import './Quote.css';

import { WorkflowVersion } from '@foxden/version-controller-client';
import React, { useContext, useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';
import { z } from 'zod';

import { useAuth } from '../../auth/AuthProvider';
import { PaymentClient } from '../../backend-client/paymentBackend';
import { PaymentGraphqlClient } from '../../backend-client/paymentGraphqlBackend';
import { RatingQuotingClient } from '../../backend-client/ratingQuotingBackend';
import Loading from '../../components/Loading/Loading';
import { BusinessInfo } from '../../generated/graphql';
import { UserPaymentPlanOptionByQuoteId } from '../../generated/payment-graphql';
import {
  AdditionalInsured as AdditionalInsuredInfo,
  GetQuoteOk,
  ResetCoverageRecommendationInput,
  UpdateCoverageInput,
  UpdateCoverageOutput
} from '../../generated/rating-quoting-graphql';
import { Context, ContextValue } from '../../utils/store';
import ErrorPage from '../Error/Error';
import AdditionalInsured from './components/AdditionalInsured';
import BuyNowButton from './components/BuyNowButton';
import Coverages from './components/Coverages';
import CustomerAgreement from './components/CustomerAgreement';
import Premium from './components/Premium';
import { SummaryContainer } from './components/Summary';

export interface CoveragesDataInput {
  name: string;
  chooseCoverage: boolean;
  finalPremium: number;
  limits: Record<string, number>;
}

type ApplicationQuoteGroup = {
  lobType: string;
  applicationId: string;
  quoteData: GetQuoteOk;
};

interface QuoteProps {
  applicationQuoteGroups: [ApplicationQuoteGroup, ...ApplicationQuoteGroup[]]; // guarantee at least one item in array
  versionData?: WorkflowVersion;
  shouldShowCustomerAgreement?: boolean;
  isTnCLinkSent?: boolean;
  businessInfo: BusinessInfo;
}

const additionalInsuedInfoSchema = z.object({
  name: z.string().min(1),
  address: z.object({
    street: z.string().min(1),
    postalCode: z.string().min(5),
    province: z.string().min(2),
    city: z.string().min(1),
    address2: z.string().nullable().optional()
  }),
  email: z
    .string()
    .refine((value) => value === '')
    .or(z.string().email())
});

// This component handles rendering of active quotes (quoteData is always defined)
// Expired quote handling is done in index.tsx

const Quote: React.FC<QuoteProps> = (props) => {
  const context: ContextValue | null = useContext(Context);

  const {
    applicationQuoteGroups,
    versionData,
    shouldShowCustomerAgreement,
    isTnCLinkSent: isTnCLinkSentProp,
    businessInfo
  } = props;

  const primaryApplicationId = applicationQuoteGroups[0].applicationId;

  const { isAuthenticated, isLoading: isAuthProviderLoading } = useAuth();

  const isCustomerAgreementActive =
    shouldShowCustomerAgreement ??
    (context ? context.isCustomerAgreementActive : false);

  const [quoteGroups, setQuoteGroups] = useState<
    [ApplicationQuoteGroup, ...ApplicationQuoteGroup[]]
  >(applicationQuoteGroups);

  // TODO: for now, use primaryQuoteResponse to drive the quote page since we only support one product. In the future, if we support multiple products endorsement, we might need to change the logic to make sure the response of each product update is correctly reflected on the page.
  const primaryQuoteResponse = quoteGroups.find(
    (g) => g.applicationId === primaryApplicationId
  )?.quoteData as GetQuoteOk;

  const [country, setCountry] = useState('');
  const [province, setProvince] = useState('');

  // TODO: handle multiple products premium
  const [premium, setPremium] = useState(primaryQuoteResponse.premium);

  const [additionalInsuredInfoList, setAdditionalInsuredInfoList] = useState<
    AdditionalInsuredInfo[] | undefined
  >(undefined);
  const [
    fillAllAdditionalInsuredInfo,
    setFillAllAdditionalInsuredInfo
  ] = useState<boolean | undefined>(undefined);
  const additionalInsuredCoverage = primaryQuoteResponse.coveragesData.find(
    (coverage) => coverage.name === 'additionalInsured'
  );
  const enableAdditionalInsured = !!additionalInsuredCoverage;

  const isEndorsement = primaryQuoteResponse.transactionType === 'Endorsement';

  const [updateCoverageData, setUpdateCoverageData] = useState<{
    data: UpdateCoverageOutput;
    applicationId: string;
  }>();
  const [
    isUpdateCoverageByRatingQuotingLoading,
    setIsUpdateCoverageByRatingQuotingLoading
  ] = useState<boolean>(false);
  const [updateCoverageError, setUpdateCoverageError] = useState<unknown>();

  const [resetRecommendationData, setResetRecommendationData] = useState<{
    data: UpdateCoverageOutput;
    applicationId: string;
  }>();
  const [
    isResetCoverageRecommendationByRatingQuotingLoading,
    setIsResetCoverageRecommendationByRatingQuotingLoading
  ] = useState<boolean>(false);
  const [
    resetRecommendationError,
    setResetRecommendationError
  ] = useState<unknown>();

  const [quoteId, setQuoteId] = useState<string | undefined>(undefined); // this quoteId is getting from getQuoteOK response

  const [paymentOptionData, setPaymentOptionData] = useState<
    Array<UserPaymentPlanOptionByQuoteId>
  >();
  const [
    isGetUserPaymentPlanOptionByQuoteIdLoading,
    setIsGetUserPaymentPlanOptionByQuoteIdLoading
  ] = useState<boolean>(false);
  const [
    getUserPaymentPlanOptionByQuoteIdError,
    setGetUserPaymentPlanOptionByQuoteIdError
  ] = useState<unknown>();
  const [
    paymentGraphqlClient,
    setPaymentGraphqlClient
  ] = useState<PaymentGraphqlClient | null>(null);

  useEffect(() => {
    if (quoteId && versionData) {
      const client = new PaymentGraphqlClient(
        versionData.paymentBackendVersion,
        quoteId
      );
      setPaymentGraphqlClient(client);
    }
  }, [quoteId, versionData]);

  useEffect(() => window.scrollTo(0, 0), []);

  useEffect(() => {
    if (context && businessInfo.companyName) {
      const { setHeaderTitle } = context;
      setHeaderTitle(businessInfo.companyName);
    }
  }, [businessInfo.companyName, context]);

  useEffect(() => {
    // assign quoteId to state
    const { region, quoteId: id } = primaryQuoteResponse;
    setQuoteId(id);

    setCountry(region == 'Canada' ? 'Canada' : 'United States of America');
    // for now, consider "Canada" as province for data record, other US states will use individual state name
    setProvince(region);
  }, [primaryQuoteResponse, country, province]);

  useEffect(() => {
    const defaultAdditionalInsuredInfoList = primaryQuoteResponse.additionalInsuredInfoList || [
      { name: '', address: undefined, email: '' }
    ];
    // only set the AdditionalInsured default value for valid cases
    if (enableAdditionalInsured && additionalInsuredCoverage.chooseCoverage) {
      setAdditionalInsuredInfoList(defaultAdditionalInsuredInfoList);
    }
  }, [
    additionalInsuredCoverage,
    enableAdditionalInsured,
    primaryQuoteResponse
  ]);

  useEffect(() => {
    if (resetRecommendationData) {
      const { data, applicationId } = resetRecommendationData;

      setQuoteGroups(
        (groups) =>
          groups.map((g) =>
            g.applicationId === applicationId
              ? {
                  ...g,
                  quoteData: {
                    ...g.quoteData,
                    coveragesData: data.coveragesData,
                    quoteId: data.quoteId,
                    premium: data.finalPremium
                  }
                }
              : g
          ) as [ApplicationQuoteGroup, ...ApplicationQuoteGroup[]]
      );

      if (applicationId === primaryApplicationId) {
        setPremium(data.finalPremium); // TODO: remove premium later on, use payable
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetRecommendationData]);

  useEffect(() => {
    if (updateCoverageData) {
      const { data, applicationId } = updateCoverageData;

      setQuoteGroups(
        (groups) =>
          groups.map((g) =>
            g.applicationId === applicationId
              ? {
                  ...g,
                  quoteData: {
                    ...g.quoteData,
                    coveragesData: data.coveragesData,
                    quoteId: data.quoteId,
                    premium: data.finalPremium
                  }
                }
              : g
          ) as [ApplicationQuoteGroup, ...ApplicationQuoteGroup[]]
      );

      if (applicationId === primaryApplicationId) {
        setPremium(data.finalPremium); // TODO: remove premium later on, use payable
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateCoverageData]);

  useEffect(() => {
    if (!isEndorsement && quoteId !== undefined) {
      setIsGetUserPaymentPlanOptionByQuoteIdLoading(true);

      const paymentClient = new PaymentClient(
        versionData?.paymentBackendVersion
      );

      paymentClient
        .getUserPaymentPlanOptionByQuoteId(quoteId)
        .then((data) => {
          setPaymentOptionData(data);
          setIsGetUserPaymentPlanOptionByQuoteIdLoading(false);
        })
        .catch((err) => {
          setGetUserPaymentPlanOptionByQuoteIdError(err);
          setIsGetUserPaymentPlanOptionByQuoteIdLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteId, versionData, isEndorsement]);

  const onUpdateCoverage = async (input: UpdateCoverageInput) => {
    // call backend to update coverage and get a new premium
    if (versionData) {
      setIsUpdateCoverageByRatingQuotingLoading(true);

      const ratingQuotingClient = new RatingQuotingClient(
        versionData.quoteBackendVersion
      );
      ratingQuotingClient
        .updateCoverage(input)
        .then((data) => {
          setUpdateCoverageData({ data, applicationId: input.applicationId });
          setIsUpdateCoverageByRatingQuotingLoading(false);
        })
        .catch((err) => {
          setUpdateCoverageError(err);
          setIsUpdateCoverageByRatingQuotingLoading(false);
        });
    }
  };

  const onResetRecommendation = async (
    input: ResetCoverageRecommendationInput
  ) => {
    if (versionData) {
      setIsResetCoverageRecommendationByRatingQuotingLoading(true);
      const ratingQuotingClient = new RatingQuotingClient(
        versionData.quoteBackendVersion
      );
      ratingQuotingClient
        .resetCoverageRecommendation(input)
        .then((data) => {
          setResetRecommendationData({
            data,
            applicationId: input.applicationId
          });
          setIsResetCoverageRecommendationByRatingQuotingLoading(false);
        })
        .catch((err) => {
          setResetRecommendationError(err);
          setIsResetCoverageRecommendationByRatingQuotingLoading(false);
        });
    }
  };

  if (resetRecommendationError || updateCoverageError) {
    return <ErrorPage />;
  }

  if (
    isUpdateCoverageByRatingQuotingLoading ||
    isResetCoverageRecommendationByRatingQuotingLoading ||
    isGetUserPaymentPlanOptionByQuoteIdLoading ||
    !quoteId ||
    isAuthProviderLoading
  ) {
    return <Loading />;
  }

  if (getUserPaymentPlanOptionByQuoteIdError) {
    throw Error('Backend error');
  }

  // Pending: this api should return total payable for all products
  const totalPayable = paymentOptionData
    ? paymentOptionData[0].totalPayable
    : premium;

  const showSendQuoteButton = isAuthenticated;
  const quoteNumber = primaryQuoteResponse.quoteNumber;

  const validateAdditionalInsured = () => {
    let validataionResult = true;
    if (
      additionalInsuredCoverage?.['chooseCoverage'] &&
      additionalInsuredInfoList
    ) {
      additionalInsuredInfoList.forEach((info) => {
        try {
          additionalInsuedInfoSchema.parse(info);
        } catch (e) {
          setFillAllAdditionalInsuredInfo(false);
          validataionResult = false;
          return;
        }
      });
    }
    return validataionResult;
  };

  if (premium <= 0 && !isEndorsement) {
    return (
      <Redirect
        to={{
          pathname: '/complete',
          state: {
            country,
            province
          }
        }}
      />
    );
  }

  return (
    <div className="relative page-container font-raleway lg:my-4">
      <div className="mt-1 quote-box">
        <div className="quote-header md:hidden text-center mb-2">
          <p className="text-2xl text-navy-dark font-bold">
            {isCustomerAgreementActive
              ? 'Client Agreements'
              : 'Tailored Quote For'}
            :
          </p>
          <p className="text-xl font-medium">{businessInfo.companyName}</p>
        </div>
        <Premium
          yearlyPremium={premium}
          isEndorsement={isEndorsement}
          paymentOptionData={paymentOptionData}
        />
        <SummaryContainer
          quoteGroups={quoteGroups}
          totalPayable={totalPayable}
          isEndorsement={isEndorsement}
        />
        {isCustomerAgreementActive && quoteId ? (
          <CustomerAgreement
            quoteId={quoteId}
            customerName={businessInfo.customerName || ''}
            email={businessInfo.email || ''}
            paymentGraphqlClient={paymentGraphqlClient}
            isTnCLinkSent={isTnCLinkSentProp ?? false}
            isEndorsement={isEndorsement}
            coverageStartDate={businessInfo?.coverageStartDate}
          />
        ) : (
          <div className="coverages-list">
            {quoteGroups.map((g) => (
              <Coverages
                key={g.quoteData.quoteId}
                coverageInput={g.quoteData}
                onUpdateCoverage={onUpdateCoverage}
                onResetRecommendation={onResetRecommendation}
                isEndorsement={isEndorsement}
                applicationId={g.applicationId}
                lobType={g.lobType}
              />
            ))}
          </div>
        )}
        {additionalInsuredCoverage && !isCustomerAgreementActive ? (
          <AdditionalInsured
            coverageData={additionalInsuredCoverage}
            onUpdateCoverage={onUpdateCoverage}
            region={primaryQuoteResponse.region}
            quoteId={primaryQuoteResponse.quoteId}
            applicationId={primaryApplicationId} // TODO: align with business to check update all lob or specific lob
            additionalInsuredInfoList={additionalInsuredInfoList}
            setAdditionalInsuredInfoList={setAdditionalInsuredInfoList}
            fillAllAdditionalInsuredInfo={fillAllAdditionalInsuredInfo}
            setFillAllAdditionalInsuredInfo={setFillAllAdditionalInsuredInfo}
            disabled={primaryQuoteResponse.disableAdditionalInsuredCheckbox}
          />
        ) : null}
        <BuyNowButton
          quoteId={quoteId}
          applicationId={primaryApplicationId}
          showSendQuoteButton={showSendQuoteButton}
          country={country}
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          businessInfo={businessInfo!}
          disabled={false}
          setQuoteId={setQuoteId}
          additionalInsuredInfoList={additionalInsuredInfoList}
          validateAdditionalInsured={validateAdditionalInsured}
          versionData={versionData}
          quoteNumber={quoteNumber}
          isCustomerAgreementActive={isCustomerAgreementActive}
          isAuthenticated={isAuthenticated}
          isAuthProviderLoading={isAuthProviderLoading}
          paymentGraphqlClient={paymentGraphqlClient}
          transactionType={primaryQuoteResponse.transactionType}
          quoteExpireDate={primaryQuoteResponse.quoteExpireDate}
        />
      </div>
    </div>
  );
};

export default Quote;
